/**
 * NotificationRouter — Bridges EventBus → bell icon notifications
 *
 * Listens to all EventBus events, matches them against declarative
 * notification rules, and pushes in-app notifications to the store.
 */

import type {
  EngineEvent,
  NotificationRule,
  NotificationRuleCondition,
  InAppNotification,
  ExtendedNotificationEventType,
} from './types';
import { eventBus } from './eventBus';

// =============================================================================
// NOTIFICATION RULES
// =============================================================================

const NOTIFICATION_RULES: NotificationRule[] = [
  // --- Polling-sourced (new items from external sync) ---
  { eventType: 'email.received', notifType: 'new_email', title: 'New email', icon: 'mail' },
  { eventType: 'message.received', notifType: 'new_message', title: 'New message', icon: 'message' },

  // --- Entity-based (from user actions or system) ---
  {
    eventType: 'entity.created',
    entityTypes: ['banktransaction'],
    notifType: 'bank_transaction_received',
    title: 'New bank transaction',
    icon: 'bank',
  },
  {
    eventType: 'entity.created',
    entityTypes: ['invoice'],
    condition: { field: 'direction', op: 'eq', value: 'in' },
    notifType: 'invoice_received',
    title: 'Invoice received',
    icon: 'invoice',
  },
  {
    eventType: 'entity.created',
    entityTypes: ['invoice'],
    condition: { field: 'direction', op: 'eq', value: 'out' },
    notifType: 'invoice_sent',
    title: 'Invoice sent',
    icon: 'invoice',
  },

  // --- Module-specific activity ---
  {
    eventType: 'entity.updated',
    entityTypes: ['task'],
    condition: { field: 'status', op: 'eq', value: 'done' },
    notifType: 'task_completed',
    title: 'Task completed',
    icon: 'task',
  },
  {
    eventType: 'entity.created',
    entityTypes: ['task'],
    condition: { field: 'assigneeId', op: 'eq', value: '{{currentUserId}}' },
    notifType: 'task_assigned',
    title: 'Task assigned to you',
    icon: 'task',
  },
  {
    eventType: 'entity.created',
    entityTypes: ['calendar'],
    condition: { field: 'participants', op: 'contains', value: '{{currentUserId}}' },
    notifType: 'calendar_event',
    title: 'Added to event',
    icon: 'calendar',
  },
  {
    eventType: 'entity.created',
    entityTypes: ['message'],
    condition: { field: 'mentions', op: 'contains', value: '{{currentUserId}}' },
    notifType: 'mention',
    title: 'You were mentioned',
    icon: 'mention',
  },
];

// =============================================================================
// NOTIFICATION ROUTER
// =============================================================================

/** Callback to push notification into the store */
export type NotificationPushFn = (notification: InAppNotification) => void;

/** Callback to check if a notification type is enabled for inApp */
export type NotificationEnabledCheckFn = (type: ExtendedNotificationEventType) => boolean;

/** Callback to check if currently in DnD period */
export type DndCheckFn = () => boolean;

interface NotificationRouterConfig {
  /** Function to push a notification to the store */
  pushNotification: NotificationPushFn;
  /** Function to check if a notification type is enabled for inApp channel */
  isEnabledForInApp?: NotificationEnabledCheckFn;
  /** Function to check if we're in Do Not Disturb */
  isInDnD?: DndCheckFn;
  /** Current user ID (for {{currentUserId}} interpolation) */
  currentUserId?: string;
}

class NotificationRouter {
  private unsubscribe: (() => void) | null = null;
  private config: NotificationRouterConfig | null = null;

  /**
   * Start routing events to notifications
   */
  start(config: NotificationRouterConfig): void {
    this.stop();
    this.config = config;

    this.unsubscribe = eventBus.on('*', (event) => {
      this.handleEvent(event);
    });
  }

  /**
   * Stop routing
   */
  stop(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  /**
   * Update the current user ID (for condition interpolation)
   */
  setCurrentUserId(userId: string): void {
    if (this.config) {
      this.config.currentUserId = userId;
    }
  }

  /**
   * Add a custom notification rule at runtime
   */
  addRule(rule: NotificationRule): void {
    NOTIFICATION_RULES.push(rule);
  }

  /**
   * Get all active rules
   */
  getRules(): NotificationRule[] {
    return [...NOTIFICATION_RULES];
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private handleEvent(event: EngineEvent): void {
    if (!this.config) return;

    // Check DnD
    if (this.config.isInDnD?.()) return;

    for (const rule of NOTIFICATION_RULES) {
      if (this.matchesRule(rule, event)) {
        // Check user preference
        if (this.config.isEnabledForInApp && !this.config.isEnabledForInApp(rule.notifType)) {
          continue;
        }

        const notification = this.buildNotification(rule, event);
        this.config.pushNotification(notification);
      }
    }
  }

  private matchesRule(rule: NotificationRule, event: EngineEvent): boolean {
    // Must match event type
    if (rule.eventType !== event.type) return false;

    // Must match entity type (if specified)
    if (rule.entityTypes && rule.entityTypes.length > 0) {
      if (!event.entityType || !rule.entityTypes.includes(event.entityType)) {
        return false;
      }
    }

    // Must match condition (if specified)
    if (rule.condition) {
      if (!this.evaluateRuleCondition(rule.condition, event)) {
        return false;
      }
    }

    return true;
  }

  private evaluateRuleCondition(condition: NotificationRuleCondition, event: EngineEvent): boolean {
    const fieldValue = this.resolveField(condition.field, event);
    let compareValue = condition.value;

    // Interpolate {{currentUserId}}
    if (typeof compareValue === 'string' && compareValue === '{{currentUserId}}') {
      compareValue = this.config?.currentUserId || '';
    }

    switch (condition.op) {
      case 'eq':
        return fieldValue === compareValue;
      case 'neq':
        return fieldValue !== compareValue;
      case 'contains':
        if (Array.isArray(fieldValue)) return fieldValue.includes(compareValue);
        if (typeof fieldValue === 'string' && typeof compareValue === 'string') {
          return fieldValue.includes(compareValue);
        }
        return false;
      case 'exists':
        return fieldValue !== undefined && fieldValue !== null;
      case 'gt':
        return typeof fieldValue === 'number' && typeof compareValue === 'number'
          ? fieldValue > compareValue
          : false;
      case 'lt':
        return typeof fieldValue === 'number' && typeof compareValue === 'number'
          ? fieldValue < compareValue
          : false;
      case 'in':
        return Array.isArray(compareValue) ? compareValue.includes(fieldValue) : false;
      case 'regex':
        if (typeof fieldValue === 'string' && typeof compareValue === 'string') {
          try { return new RegExp(compareValue).test(fieldValue); } catch { return false; }
        }
        return false;
      default:
        return false;
    }
  }

  private resolveField(path: string, event: EngineEvent): unknown {
    // Try event.data first, then event root
    const dataValue = getNestedValue(event.data, path);
    if (dataValue !== undefined) return dataValue;
    return getNestedValue(event as unknown as Record<string, unknown>, path);
  }

  private buildNotification(rule: NotificationRule, event: EngineEvent): InAppNotification {
    const data = event.data || {};
    const title = rule.title;
    const message = this.buildMessage(rule, event);

    return {
      id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: rule.notifType,
      title,
      message,
      entityType: event.entityType,
      entityId: event.entityId,
      read: false,
      createdAt: new Date().toISOString(),
      module: event.entityType,
      icon: rule.icon,
    };
  }

  private buildMessage(rule: NotificationRule, event: EngineEvent): string {
    const data = event.data || {};
    const name = (data.name || data.title || data.subject || event.entityId || '') as string;

    switch (rule.notifType) {
      case 'new_email':
        return `From: ${data.from || 'Unknown'} — ${data.subject || 'No subject'}`;
      case 'new_message':
        return `${data.senderName || 'Someone'}: ${data.preview || data.text || ''}`;
      case 'bank_transaction_received':
        return `${data.description || 'Transaction'} — ${data.amount || ''}`;
      case 'invoice_received':
      case 'invoice_sent':
        return `${name} — ${data.amount || ''}`;
      case 'task_completed':
        return `"${name}" has been completed`;
      case 'task_assigned':
        return `"${name}" was assigned to you`;
      case 'calendar_event':
        return `You were added to "${name}"`;
      case 'mention':
        return `You were mentioned in "${name}"`;
      default:
        return name ? `${name}` : `${rule.title}`;
    }
  }
}

function getNestedValue(obj: unknown, path: string): unknown {
  if (obj == null) return undefined;
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * Singleton NotificationRouter instance
 */
export const notificationRouter = new NotificationRouter();
